import React, { useCallback, useState } from 'react';
import { CircularProgress, Grid, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { MUIDataTableColumn } from 'mui-datatables';
import InformativeCard from '../InformativeCard';
import clipboard from '../../../../assets/images/clipboard.png';
import CustomDialog from '../../../../components/CustomDialog';
import CustomDataTable from '../../../../components/CustomDataTable';
import { CondominiumReducerData } from '../../../../store/reducers/condominiumReducer';
import { EmployeesReducerData } from '../../../../store/reducers/employeesReducer';
import { AppState } from '../../../../store';
import { findAllSignedTerms } from '../../../../services/personnelDepartment.service';
import { isCancel } from '../../../../services/api';

interface SignedTermsTableData {
  employeeCode: number;
  employeeName: string;
  signedDate: string;
}

const EmployeesTermSignedCard: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [termsSigned, setTermsSigned] = useState<SignedTermsTableData[]>([]);

  const { condominiumSelected }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );

  const { employees }: EmployeesReducerData = useSelector(
    (state: AppState) => state.employeesReducer,
  );

  const getData = useCallback(async () => {
    try {
      if (condominiumSelected?.referencia) {
        setTermsSigned([]);
        setOpenDialog(true);
        setLoading(true);
        const { data } = await findAllSignedTerms(
          condominiumSelected?.referencia,
        );

        const newestTermsVersion = Math.max.apply(
          Math,
          data.map(item => {
            return item.idTermoUso;
          }),
        );
        const newestsSignedTerms = data.filter(
          item => item.idTermoUso === newestTermsVersion,
        );
        const tableDataAux: SignedTermsTableData[] = [];
        newestsSignedTerms.forEach(term => {
          employees.forEach(employee => {
            if (term.idUsuario === employee.numeroCadastro) {
              tableDataAux.push({
                employeeCode: term.idUsuario,
                employeeName: employee.nome,
                signedDate: term.dataHoraAssinatura,
              });
            }
          });
        });
        setTermsSigned(tableDataAux);
      }
    } catch (error) {
      if (!isCancel(error)) {
        console.log(error);
      }
    } finally {
      setLoading(false);
    }
  }, [condominiumSelected, employees]);

  const handleCardClick = useCallback(async () => {
    getData();
  }, [getData]);

  const columns: MUIDataTableColumn[] = [
    {
      name: 'employeeCode',
      label: 'Número de Cadastro',
    },
    {
      name: 'employeeName',
      label: 'Nome',
    },
    {
      name: 'signedDate',
      label: 'Data de Assinatura',
    },
  ];

  return (
    <>
      <InformativeCard
        imgAltText="Termos Assinados"
        imgSrc={clipboard}
        cardTitle="Funcionários que assinaram o termo de uso"
        cardText="Verifique a lista dos funcionários que já assinaram os termos de uso do aplicativo."
        handleCardClick={handleCardClick}
      />
      <CustomDialog
        open={openDialog}
        handleClose={() => setOpenDialog(false)}
        handleConfirm={() => setOpenDialog(false)}
        showOneButton
        maxWidth="md"
        okButtonLabel="Fechar"
        showCloseButton
      >
        <CustomDataTable
          title={
            <Grid container direction="row">
              <Typography variant="h6">
                Funcionários que assinaram o termo de uso
              </Typography>
              {loading && (
                <CircularProgress
                  size={20}
                  style={{ marginLeft: 15, position: 'relative', top: 4 }}
                />
              )}
            </Grid>
          }
          data={termsSigned}
          columns={columns}
          options={{ elevation: 0, download: true }}
        />
      </CustomDialog>
    </>
  );
};

export default EmployeesTermSignedCard;
